import { useMemo, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Close, EventRepeat, PauseCircleOutline, PlayCircleOutline, TrendingUp } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as billing from '@/api/recurringBilling';
import type { IncreaseMode, RecurringRule, RecurringRuleStatus } from '@/api/recurringBilling';
import { searchRecipients } from '@/api/recipients';
import { RecurrentesBuilder } from '@/components/RecurrentesBuilder';
import { toApiError } from '@/api/client';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { ProductFeatureRouteGuard } from '@/components/ProductFeatureRouteGuard';
import { formatCurrency } from '@/lib/formatters';
import { useCurrentCuit } from '@/auth/CuitContext';

export const Route = createFileRoute('/_authenticated/recurrentes/')({
  component: RecurrentesPage,
});

type StatusFilter = RecurringRuleStatus | 'All';

const STATUS: Record<RecurringRuleStatus, { label: string; color: 'success' | 'default' | 'warning' | 'info' }> = {
  Active: { label: 'Activa', color: 'success' },
  Paused: { label: 'En pausa', color: 'warning' },
  Draft: { label: 'Borrador', color: 'info' },
  Completed: { label: 'Finalizada', color: 'default' },
};

const FREQUENCY: Record<billing.RecurringFrequency, string> = {
  Weekly: 'Semanal',
  Biweekly: 'Quincenal',
  Monthly: 'Mensual',
  Quarterly: 'Trimestral',
  SemiAnnual: 'Semestral',
  Annual: 'Anual',
};

const APPROVAL: Record<billing.ApprovalMode, string> = { Automatic: 'Automática', Manual: 'Manual' };
const INCREASE_MODE: Record<IncreaseMode, string> = { Percentage: '%', FixedAmount: '+ fijo', AbsoluteValue: '= absoluto' };

function formatDateOnly(d: string | null | undefined): string {
  if (!d) return '—';
  const parsed = new Date(`${d}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function increaseTag(rule: RecurringRule): string | null {
  if (rule.increaseVersions.length === 0) return null;
  const latest = [...rule.increaseVersions].sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate))[0]!;
  if (latest.mode === 'Percentage') return `+${latest.value}%`;
  if (latest.mode === 'FixedAmount') return `+${formatCurrency(latest.value, rule.currency)}`;
  return `= ${formatCurrency(latest.value, rule.currency)}`;
}

function RecurrentesPage(): JSX.Element {
  return (
    <ProductFeatureRouteGuard
      feature="recurring-billing"
      title="Facturación recurrente no incluida"
      description="Tu producto o plan actual no incluye reglas recurrentes. Revisá las opciones con automatización habilitada."
      products={['Empresa']}
      commercialProducts={['Monotributo', 'ResponsableInscripto']}
    >
      <RecurrentesContent />
    </ProductFeatureRouteGuard>
  );
}

function RecurrentesContent(): JSX.Element {
  const queryClient = useQueryClient();
  const { currentCuitProfileId } = useCurrentCuit();
  const [filter, setFilter] = useState<StatusFilter>('Active');
  const [selected, setSelected] = useState<RecurringRule | null>(null);
  const [building, setBuilding] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const statusParam: RecurringRuleStatus | undefined = filter === 'All' ? undefined : filter;
  const rulesQ = useQuery({
    queryKey: ['recurrentes', filter],
    queryFn: () => billing.listRecurringRules(statusParam, 0, 200),
  });
  const recipientsQ = useQuery({
    queryKey: ['recipients-map', currentCuitProfileId],
    queryFn: () => searchRecipients('', currentCuitProfileId!, 200),
    enabled: Boolean(currentCuitProfileId),
    staleTime: 5 * 60_000,
  });

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of recipientsQ.data ?? []) m.set(r.id, r.legalName);
    return m;
  }, [recipientsQ.data]);

  // Recurrentes de Empresa = reglas SIN studyClientId (las que sí lo tienen son Honorarios del estudio).
  const rules = useMemo(
    () => (rulesQ.data?.items ?? []).filter(
      (r) => r.studyClientId === null && (!currentCuitProfileId || r.cuitProfileId === currentCuitProfileId),
    ),
    [currentCuitProfileId, rulesQ.data],
  );

  const activeCount = rules.filter((r) => r.status === 'Active').length;
  const pausedCount = rules.filter((r) => r.status === 'Paused').length;
  const nextDue = rules.filter((r) => r.status === 'Active' && r.nextRunAt).map((r) => r.nextRunAt!).sort((a, b) => a.localeCompare(b))[0];

  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: ['recurrentes'] });

  if (rulesQ.isLoading) return <LoadingScreen message="Cargando recurrentes…" />;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventRepeat color="primary" /> Facturas recurrentes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Automatizá facturas periódicas a tus clientes, con importes e incrementos programados.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setBuilding(true)}>Nueva regla</Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Kpi label="Reglas activas" value={String(activeCount)} hint={`de ${rules.length}`} icon={<PlayCircleOutline color="success" />} />
        <Kpi label="En pausa" value={String(pausedCount)} hint="sin emitir" icon={<PauseCircleOutline color="warning" />} />
        <Kpi label="Próximo vencimiento" value={formatDateOnly(nextDue)} hint="regla activa" icon={<EventRepeat color="action" />} />
      </Grid>

      <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v: StatusFilter | null) => v && setFilter(v)} sx={{ mb: 2 }}>
        <ToggleButton value="Active">Activas</ToggleButton>
        <ToggleButton value="Paused">En pausa</ToggleButton>
        <ToggleButton value="Draft">Borradores</ToggleButton>
        <ToggleButton value="Completed">Finalizadas</ToggleButton>
        <ToggleButton value="All">Todas</ToggleButton>
      </ToggleButtonGroup>

      {rulesQ.error ? (
        <MuiAlert severity="error">{toApiError(rulesQ.error).message}</MuiAlert>
      ) : rules.length === 0 ? (
        <EmptyState
          icon={<EventRepeat fontSize="inherit" />}
          title="Sin reglas recurrentes"
          description="Las reglas de facturación recurrente a tus clientes aparecen acá. El alta guiada llega con el builder de recurrentes."
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cliente / concepto</TableCell>
                <TableCell>Frecuencia</TableCell>
                <TableCell>Próximo</TableCell>
                <TableCell align="right">Importe neto</TableCell>
                <TableCell>Incremento</TableCell>
                <TableCell>Aprobación</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((r) => {
                const tag = increaseTag(r);
                return (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(r)}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{nameById.get(r.customerId) ?? 'Cliente'}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.items[0]?.description ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>{FREQUENCY[r.schedule.frequency]}</TableCell>
                    <TableCell>{formatDateOnly(r.nextRunAt)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(r.baseNetTotal, r.currency)}</TableCell>
                    <TableCell>{tag ? <Chip size="small" icon={<TrendingUp />} label={tag} variant="outlined" color="info" /> : <Typography variant="caption" color="text.secondary">—</Typography>}</TableCell>
                    <TableCell>{APPROVAL[r.approvalMode]}</TableCell>
                    <TableCell><Chip size="small" label={STATUS[r.status].label} color={STATUS[r.status].color} variant="outlined" /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selected && (
        <RuleDetailDialog
          rule={selected}
          customerName={nameById.get(selected.customerId) ?? 'Cliente'}
          onClose={() => setSelected(null)}
          onChanged={(updated: RecurringRule) => { setSelected(updated); refresh(); }}
          onToast={setToast}
        />
      )}

      {building && (
        <RecurrentesBuilder
          onClose={() => setBuilding(false)}
          onCreated={() => { setBuilding(false); refresh(); setToast({ message: 'Regla creada.', severity: 'success' }); }}
          onError={(msg) => setToast({ message: msg, severity: 'error' })}
        />
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">{toast?.message}</MuiAlert>
      </Snackbar>
    </Box>
  );
}

function Kpi({ label, value, hint, icon }: { label: string; value: string; hint: string; icon: JSX.Element }): JSX.Element {
  return (
    <Grid item xs={12} sm={4}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {icon}
          <Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h6">{value}</Typography>
            <Typography variant="caption" color="text.secondary">{hint}</Typography>
          </Box>
        </Stack>
      </Paper>
    </Grid>
  );
}

function RuleDetailDialog({
  rule,
  customerName,
  onClose,
  onChanged,
  onToast,
}: {
  rule: RecurringRule;
  customerName: string;
  onClose: () => void;
  onChanged: (updated: RecurringRule) => void;
  onToast: (t: { message: string; severity: 'success' | 'error' }) => void;
}): JSX.Element {
  const [occurrences, setOccurrences] = useState<billing.SimulatedOccurrence[] | null>(null);
  const [showIncrease, setShowIncrease] = useState(false);

  const handleError = (err: unknown): void => onToast({ message: toApiError(err).message, severity: 'error' });

  const lifecycleMut = useMutation({
    mutationFn: (action: 'activate' | 'pause' | 'resume' | 'complete') => {
      if (action === 'activate') return billing.activateRule(rule.id);
      if (action === 'pause') return billing.pauseRule(rule.id);
      if (action === 'resume') return billing.resumeRule(rule.id);
      return billing.completeRule(rule.id);
    },
    onSuccess: (updated) => { onChanged(updated); onToast({ message: 'Regla actualizada.', severity: 'success' }); },
    onError: handleError,
  });

  const simulateMut = useMutation({
    mutationFn: () => billing.simulateRule(rule.id, 6),
    onSuccess: setOccurrences,
    onError: handleError,
  });

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">{customerName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {FREQUENCY[rule.schedule.frequency]} · {APPROVAL[rule.approvalMode]} · {STATUS[rule.status].label}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>Conceptos</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow><TableCell>Descripción</TableCell><TableCell align="right">Cant.</TableCell><TableCell align="right">P. unit.</TableCell><TableCell align="right">Subtotal</TableCell></TableRow></TableHead>
                <TableBody>
                  {rule.items.map((it, i) => (
                    <TableRow key={i}>
                      <TableCell>{it.description}</TableCell>
                      <TableCell align="right">{it.quantity}</TableCell>
                      <TableCell align="right">{formatCurrency(it.unitPrice, rule.currency)}</TableCell>
                      <TableCell align="right">{formatCurrency(it.quantity * it.unitPrice, rule.currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Neto base: <strong>{formatCurrency(rule.baseNetTotal, rule.currency)}</strong> · PV {rule.pointOfSale} · {rule.documentKind}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Cronograma</Typography>
            <Typography variant="body2" color="text.secondary">
              Desde {formatDateOnly(rule.schedule.startDate)}{rule.schedule.endDate ? ` hasta ${formatDateOnly(rule.schedule.endDate)}` : ' (sin fin)'} ·
              {' '}día ancla {rule.schedule.anchorDay} · próxima emisión {formatDateOnly(rule.nextRunAt)}
            </Typography>
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">Incrementos programados</Typography>
              <Button size="small" startIcon={<TrendingUp />} onClick={() => setShowIncrease((v) => !v)}>Programar incremento</Button>
            </Stack>
            {rule.increaseVersions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Sin incrementos programados.</Typography>
            ) : (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {[...rule.increaseVersions].sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate)).map((v, i) => (
                  <Typography key={i} variant="body2">
                    {formatDateOnly(v.effectiveDate)} · {INCREASE_MODE[v.mode]} {v.value}
                    {v.cap != null ? ` · tope ${formatCurrency(v.cap, rule.currency)}` : ''}
                    {v.floor != null ? ` · piso ${formatCurrency(v.floor, rule.currency)}` : ''}
                  </Typography>
                ))}
              </Stack>
            )}
            {showIncrease && (
              <ScheduleIncreaseForm
                rule={rule}
                onDone={(updated) => { setShowIncrease(false); onChanged(updated); onToast({ message: 'Incremento programado.', severity: 'success' }); }}
                onError={handleError}
              />
            )}
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">Próximas emisiones (simulación)</Typography>
              <Button size="small" onClick={() => simulateMut.mutate()} disabled={simulateMut.isPending}>
                {simulateMut.isPending ? <CircularProgress size={16} /> : 'Simular 6'}
              </Button>
            </Stack>
            {occurrences && (
              <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Fecha</TableCell><TableCell align="right">Neto</TableCell><TableCell align="right">IVA</TableCell><TableCell align="right">Total</TableCell></TableRow></TableHead>
                  <TableBody>
                    {occurrences.map((o, i) => (
                      <TableRow key={i}>
                        <TableCell>{formatDateOnly(o.date)}</TableCell>
                        <TableCell align="right">{formatCurrency(o.netAmount, rule.currency)}</TableCell>
                        <TableCell align="right">{formatCurrency(o.vatAmount, rule.currency)}</TableCell>
                        <TableCell align="right">{formatCurrency(o.total, rule.currency)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          <Divider />

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {rule.status === 'Draft' && <Button variant="contained" startIcon={<PlayCircleOutline />} disabled={lifecycleMut.isPending} onClick={() => lifecycleMut.mutate('activate')}>Activar</Button>}
            {rule.status === 'Active' && <Button variant="outlined" startIcon={<PauseCircleOutline />} disabled={lifecycleMut.isPending} onClick={() => lifecycleMut.mutate('pause')}>Pausar</Button>}
            {rule.status === 'Paused' && <Button variant="contained" startIcon={<PlayCircleOutline />} disabled={lifecycleMut.isPending} onClick={() => lifecycleMut.mutate('resume')}>Reanudar</Button>}
            {(rule.status === 'Active' || rule.status === 'Paused') && (
              <Tooltip title="Marca la regla como finalizada; deja de emitir.">
                <Button color="inherit" disabled={lifecycleMut.isPending} onClick={() => lifecycleMut.mutate('complete')}>Finalizar</Button>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleIncreaseForm({
  rule,
  onDone,
  onError,
}: {
  rule: RecurringRule;
  onDone: (updated: RecurringRule) => void;
  onError: (err: unknown) => void;
}): JSX.Element {
  const [effectiveDate, setEffectiveDate] = useState('');
  const [mode, setMode] = useState<IncreaseMode>('Percentage');
  const [value, setValue] = useState('');

  const mut = useMutation({
    mutationFn: () => billing.scheduleIncrease(rule.id, { effectiveDate, mode, value: Number(value) }),
    onSuccess: onDone,
    onError,
  });

  const valid = effectiveDate !== '' && value !== '' && !Number.isNaN(Number(value));

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 1.5 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField label="Fecha de vigencia" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField select label="Modo" size="small" fullWidth value={mode} onChange={(e) => setMode(e.target.value as IncreaseMode)}>
            <MenuItem value="Percentage">Porcentaje (%)</MenuItem>
            <MenuItem value="FixedAmount">Monto fijo (+)</MenuItem>
            <MenuItem value="AbsoluteValue">Valor absoluto (=)</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label={mode === 'Percentage' ? 'Porcentaje' : 'Importe'} type="number" size="small" fullWidth value={value} onChange={(e) => setValue(e.target.value)} />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" size="small" disabled={!valid || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending ? <CircularProgress size={16} /> : 'Programar'}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
}
